export interface IMultiAnalysisMainPanelFlexGrow {
  time: number;
  graphic: number;
  trends: number;
}

export const DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW: IMultiAnalysisMainPanelFlexGrow = {
  time: 1,
  graphic: 6,
  trends: 3,
};

const SESSION_STORAGE_KEY = "sp.multiAnalysis.mainPanelFlexGrow";

export const MULTI_ANALYSIS_MAIN_PANEL_MIN_HEIGHTS = {
  time: 48,
  graphic: 180,
  trends: 140,
} as const;

export const MULTI_ANALYSIS_MAIN_PANELS_GAP_PX = 10;
export const MULTI_ANALYSIS_TRENDS_PANEL_COLLAPSED_HEIGHT = 40;

export type TMultiAnalysisMainPanelResizeBoundary = "time-graphic" | "graphic-trends";

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const isAtMinPanelHeight = (height: number, minHeight: number): boolean => height <= minHeight + 0.5;

const toFlexGrowWeights = (heights: IMultiAnalysisMainPanelFlexGrow): IMultiAnalysisMainPanelFlexGrow => {
  const sum = heights.time + heights.graphic + heights.trends;

  if (sum <= 0) {
    return heights;
  }

  const scale = 8 / sum;

  return {
    time: heights.time * scale,
    graphic: heights.graphic * scale,
    trends: heights.trends * scale,
  };
};

export const getMultiAnalysisMainPanelsExpandableHeight = (
  containerHeight: number,
  trendsExpanded: boolean,
  trendsVisible = true,
): number => {
  const gapCount = trendsVisible && trendsExpanded ? 2 : 1;
  const gapsHeight = gapCount * MULTI_ANALYSIS_MAIN_PANELS_GAP_PX;
  const collapsedTrendsHeight = trendsVisible && !trendsExpanded ? MULTI_ANALYSIS_TRENDS_PANEL_COLLAPSED_HEIGHT : 0;

  return Math.max(0, containerHeight - gapsHeight - collapsedTrendsHeight);
};

export const getMultiAnalysisMainPanelHeights = (
  flexGrow: IMultiAnalysisMainPanelFlexGrow,
  expandableHeight: number,
  trendsExpanded: boolean,
): IMultiAnalysisMainPanelFlexGrow => {
  const activeFlexTotal = flexGrow.time + flexGrow.graphic + (trendsExpanded ? flexGrow.trends : 0);

  if (activeFlexTotal <= 0 || expandableHeight <= 0) {
    return {
      time: 0,
      graphic: 0,
      trends: trendsExpanded ? 0 : MULTI_ANALYSIS_TRENDS_PANEL_COLLAPSED_HEIGHT,
    };
  }

  return {
    time: (flexGrow.time / activeFlexTotal) * expandableHeight,
    graphic: (flexGrow.graphic / activeFlexTotal) * expandableHeight,
    trends: trendsExpanded
      ? (flexGrow.trends / activeFlexTotal) * expandableHeight
      : MULTI_ANALYSIS_TRENDS_PANEL_COLLAPSED_HEIGHT,
  };
};

const resizeAdjacentPairHeights = (
  primaryHeight: number,
  secondaryHeight: number,
  deltaY: number,
  minPrimary: number,
  minSecondary: number,
): { nextPrimaryHeight: number; nextSecondaryHeight: number; unappliedDelta: number } => {
  const pairTotal = primaryHeight + secondaryHeight;
  const nextPrimaryHeight = clamp(primaryHeight + deltaY, minPrimary, pairTotal - minSecondary);

  return {
    nextPrimaryHeight,
    nextSecondaryHeight: pairTotal - nextPrimaryHeight,
    unappliedDelta: deltaY - (nextPrimaryHeight - primaryHeight),
  };
};

export const resizeMultiAnalysisMainPanelFlexGrow = (
  flexGrow: IMultiAnalysisMainPanelFlexGrow,
  expandableHeight: number,
  boundary: TMultiAnalysisMainPanelResizeBoundary,
  deltaY: number,
  trendsExpanded: boolean,
): IMultiAnalysisMainPanelFlexGrow => {
  if (expandableHeight <= 0 || deltaY === 0) {
    return flexGrow;
  }

  const heights = getMultiAnalysisMainPanelHeights(flexGrow, expandableHeight, trendsExpanded);
  const minTime = MULTI_ANALYSIS_MAIN_PANEL_MIN_HEIGHTS.time;
  const minGraphic = MULTI_ANALYSIS_MAIN_PANEL_MIN_HEIGHTS.graphic;
  const minTrends = MULTI_ANALYSIS_MAIN_PANEL_MIN_HEIGHTS.trends;

  if (boundary === "time-graphic") {
    const { nextPrimaryHeight, nextSecondaryHeight, unappliedDelta } = resizeAdjacentPairHeights(
      heights.time,
      heights.graphic,
      deltaY,
      minTime,
      minGraphic,
    );

    let nextTimeHeight = nextPrimaryHeight;
    let nextGraphicHeight = nextSecondaryHeight;
    let nextTrendsHeight = trendsExpanded ? heights.trends : flexGrow.trends;

    if (
      unappliedDelta > 0 &&
      trendsExpanded &&
      isAtMinPanelHeight(heights.graphic, minGraphic) &&
      isAtMinPanelHeight(nextGraphicHeight, minGraphic)
    ) {
      const transfer = Math.min(unappliedDelta, heights.trends - minTrends);

      if (transfer > 0) {
        nextTimeHeight = nextPrimaryHeight + transfer;
        nextGraphicHeight = heights.graphic;
        nextTrendsHeight = heights.trends - transfer;
      }
    }

    return toFlexGrowWeights({
      time: nextTimeHeight,
      graphic: nextGraphicHeight,
      trends: nextTrendsHeight,
    });
  }

  if (!trendsExpanded) {
    return flexGrow;
  }

  const { nextPrimaryHeight, nextSecondaryHeight, unappliedDelta } = resizeAdjacentPairHeights(
    heights.graphic,
    heights.trends,
    deltaY,
    minGraphic,
    minTrends,
  );

  let nextGraphicHeight = nextPrimaryHeight;
  let nextTrendsHeight = nextSecondaryHeight;
  let nextTimeHeight = heights.time;

  if (
    unappliedDelta < 0 &&
    isAtMinPanelHeight(heights.graphic, minGraphic) &&
    isAtMinPanelHeight(nextGraphicHeight, minGraphic)
  ) {
    const transfer = Math.min(-unappliedDelta, heights.time - minTime);

    if (transfer > 0) {
      nextTrendsHeight = nextSecondaryHeight + transfer;
      nextGraphicHeight = heights.graphic;
      nextTimeHeight = heights.time - transfer;
    }
  }

  return toFlexGrowWeights({
    time: nextTimeHeight,
    graphic: nextGraphicHeight,
    trends: nextTrendsHeight,
  });
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeFlexGrowValue = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;

export const normalizeMultiAnalysisMainPanelFlexGrow = (raw: unknown): IMultiAnalysisMainPanelFlexGrow => {
  if (!isPlainObject(raw)) {
    return { ...DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW };
  }

  return {
    time: normalizeFlexGrowValue(raw.time, DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW.time),
    graphic: normalizeFlexGrowValue(raw.graphic, DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW.graphic),
    trends: normalizeFlexGrowValue(raw.trends, DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW.trends),
  };
};

export const readMultiAnalysisMainPanelFlexGrowFromSession = (): IMultiAnalysisMainPanelFlexGrow => {
  if (typeof window === "undefined") {
    return { ...DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW };
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

    return raw
      ? normalizeMultiAnalysisMainPanelFlexGrow(JSON.parse(raw))
      : { ...DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW };
  } catch {
    return { ...DEFAULT_MULTI_ANALYSIS_MAIN_PANEL_FLEX_GROW };
  }
};

export const writeMultiAnalysisMainPanelFlexGrowToSession = (flexGrow: IMultiAnalysisMainPanelFlexGrow): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify(normalizeMultiAnalysisMainPanelFlexGrow(flexGrow)),
    );
  } catch {
    // Ignore quota or privacy mode errors.
  }
};

type TMultiAnalysisMainPanelKey = keyof IMultiAnalysisMainPanelFlexGrow;

interface IGetMultiAnalysisMainPanelSxArgs {
  panel: TMultiAnalysisMainPanelKey;
  hasMeasuredLayout: boolean;
  panelHeights: IMultiAnalysisMainPanelFlexGrow | null;
  panelFlexGrow: IMultiAnalysisMainPanelFlexGrow;
  expanded?: boolean;
  sx?: Record<string, unknown>;
}

export const getMultiAnalysisMainPanelSx = ({
  panel,
  hasMeasuredLayout,
  panelHeights,
  panelFlexGrow,
  expanded = true,
  sx,
}: IGetMultiAnalysisMainPanelSxArgs): Record<string, unknown> | undefined => {
  if (!expanded) {
    return undefined;
  }

  const minHeight = MULTI_ANALYSIS_MAIN_PANEL_MIN_HEIGHTS[panel];

  if (hasMeasuredLayout && panelHeights) {
    return {
      flex: "0 0 auto",
      height: panelHeights[panel],
      minHeight,
      ...sx,
    };
  }

  return {
    flex: `${panelFlexGrow[panel]} 1 0`,
    minHeight,
    ...sx,
  };
};
